import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext, M_Button, M_InputTextField, M_Fieldset, M_DrawerTableTextField, M_Header, M_Select } from 'src/M_Components'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import './transportClausesCrud.css'

export function TransportClausesCrud({ handleTabClose, toastMessages }) {
    const [id, setId] = useState(sessionStorage.getItem('selectedTab'))
    const ctx = useContext(LoadingContext)
    const { t } = useTranslation()

    const bufferService = new BufferService('TransportClause')
    const detailService = new BufferService('TransportClauseDetail')

    const [data, setData] = useState({})
    const [details, setDetails] = useState([])
    const [errors, setErrors] = useState({})
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [operation, setOperation] = useState('Create')
    const [rowId, setRowId] = useState(null)
    const [isFirstTime, setIsFirstTime] = useState(true)
    const [newDetail, setNewDetail] = useState({})

    const isEditMode = operation === 'Edit' || operation === 'Copy'
    const isCreateMode = operation === 'Create'
    const isViewMode = operation === 'View'

    const clauseDocumentOptions = [
        { label: t('None'), value: '' },
        { label: 'BL', value: 'BL' },
        { label: 'Manifest', value: 'MANIFEST' },
        { label: 'BL & Manifest', value: 'BLMANIFEST' },
        { label: 'BL LCL', value: 'BLLCL' },
        { label: 'Manifest LCL', value: 'MANIFESTLCL' },
        { label: 'BL & Manifest LCL', value: 'BLMANIFESTLCL' },
        { label: 'BL FCL', value: 'BLFCL' },
        { label: 'Manifest FCL', value: 'MANIFESTFCL' },
        { label: 'BL & Manifest FCL', value: 'BLMANIFESTFCL' },
    ]

    const sessionKeys = ['operation', 'rowId', 'data', 'details']

    // Save state to sessionStorage
    useEffect(() => {
        if (!isFirstTime) {
            if (operation) sessionStorage.setItem(`${id}operation`, JSON.stringify(operation))
            if (rowId) sessionStorage.setItem(`${id}rowId`, JSON.stringify(rowId))
            if (data && Object.keys(data).length > 0) sessionStorage.setItem(`${id}data`, JSON.stringify(data))
            if (details && details.length > 0) sessionStorage.setItem(`${id}details`, JSON.stringify(details))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, operation, rowId, data, details])

    // Detect tab switch
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const newId = sessionStorage.getItem('selectedTab')
        if (newId !== id) {
            setId(newId)
            getSessionStorageData(newId)
        }
    })

    // Initial load
    useEffect(() => {
        if (isFirstTime) {
            setIsFirstTime(false)
            sessionStorage.setItem(`tab${id}`, JSON.stringify(sessionKeys))
            getSessionStorageData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getSessionStorageData = (sessionID) => {
        const tempOp = JSON.parse(sessionStorage.getItem(`${sessionID}operation`))
        const tempRowId = JSON.parse(sessionStorage.getItem(`${sessionID}rowId`))
        const tempData = JSON.parse(sessionStorage.getItem(`${sessionID}data`))
        const tempDetails = JSON.parse(sessionStorage.getItem(`${sessionID}details`))

        // Session has data - restore from sessionStorage
        if (tempOp && tempData && Object.keys(tempData).length > 0) {
            setOperation(tempOp)
            setRowId(tempRowId)
            setData(tempData)
            setDetails(tempDetails || [])
            return
        }

        // First-time open - use passValues from list
        const passData = ctx.passValues
        if (passData?.Operation) {
            const op = passData.Operation
            setOperation(op)
            ctx.setPassValues(null)

            if (op === 'Create') {
                handleOpenCreate()
            } else if (passData.Row) {
                setRowId(passData.Row.Id)
                handleOpenEdit(passData.Row.Id, op)
            }
        } else {
            handleOpenCreate()
        }
    }

    const handleOpenCreate = () => {
        setData({
            ClauseCode: '',
            ClauseName: '',
            ClauseOrder: '',
            TradeLineCode: '',
            TradeCode: '',
            TradeBound: '',
            LineCode: '',
            LoadingId: '',
            DischargeId: '',
            ReceiptId: '',
            DeliveryId: '',
            StartDate: '',
            EndDate: '',
            EquipmentClassId: '',
            EquipmentTypeId: '',
            ClauseDocument: ''
        })
        setDetails([])
    }

    const handleOpenEdit = (recordId, op) => {
        ctx.setIsLoad(true)
        bufferService.getId(recordId).then((response) => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
                ctx.setIsLoad(false)
                handleTabClose(JSON.parse(id))
                return
            }

            const transportClause = {
                ...response,
                StartDate: response.StartDate ? response.StartDate.slice(0, 10) : '',
                EndDate: response.EndDate ? response.EndDate.slice(0, 10) : '',
                Loading_LocationCode: response.LoadingPortCode || '',
                Discharge_LocationCode: response.DischargePortCode || '',
                Receipt_LocationCode: response.ReceiptPortCode || '',
                Delivery_LocationCode: response.DeliveryPortCode || '',
                EquipmentClass_Code: response.EquipmentClassCode || '',
                EquipmentType_Code: response.EquipmentTypeCode || ''
            }

            if ((op || operation) === 'Copy') {
                delete transportClause.Id
            }

            setData(transportClause)

            // Load details
            loadDetails(recordId)
        }).catch(err => {
            ctx.toastMessages('error', err.message, 'Error')
            ctx.setIsLoad(false)
        })
    }

    const loadDetails = (transportClauseId) => {
        detailService.filter(`/by-transport-clause/${transportClauseId}`).then(response => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
                setDetails([])
            } else if (Array.isArray(response)) {
                setDetails(response)
            } else if (response && response.Items) {
                setDetails(response.Items)
            } else {
                setDetails([])
            }
            ctx.setIsLoad(false)
        }).catch(err => {
            ctx.toastMessages('error', '', err.message)
            setDetails([])
            ctx.setIsLoad(false)
        })
    }

    const handleChange = (field, value) => {
        setData(prev => ({ ...prev, [field]: value }))
        setHasUnsavedChanges(true)
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[field]
                return newErrors
            })
        }
    }

    // Validação consolidada antes de salvar
    const validateBeforeSave = () => {
        const newErrors = {}
        
        // 1. Validar campos obrigatórios
        if (!data.ClauseCode || data.ClauseCode.trim() === '') {
            newErrors.ClauseCode = t('ClauseCodeRequired')
        }
        if (!data.StartDate) {
            newErrors.StartDate = t('StartDateRequired')
        }
        
        // 2. Validar tamanhos máximos
        if (data.ClauseCode && data.ClauseCode.length > 8) {
            newErrors.ClauseCode = t('ClauseCodeMaxLength')
        }
        if (data.ClauseName && data.ClauseName.length > 64) {
            newErrors.ClauseName = t('ClauseNameMaxLength')
        }
        if (data.ClauseDocument && !clauseDocumentOptions.some(o => o.value === data.ClauseDocument)) {
            newErrors.ClauseDocument = t('ClauseDocumentInvalid')
        }
        
        // 3. Validar lógica de negócio - range de datas
        if (data.StartDate && data.EndDate) {
            const startDate = new Date(data.StartDate)
            const endDate = new Date(data.EndDate)
            if (startDate > endDate) {
                newErrors.EndDate = t('EndDateAfterStartDate')
            }
        }
        
        // 4. Validar ClauseOrder
        if (data.ClauseOrder !== '' && data.ClauseOrder !== null && data.ClauseOrder !== undefined) {
            const orderNum = Number(data.ClauseOrder)
            if (isNaN(orderNum) || orderNum < 0) {
                newErrors.ClauseOrder = t('ClauseOrderPositive')
            }
        }
        
        setErrors(newErrors)
        
        // Se houver erros, exibir mensagem consolidada
        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join('\n')
            ctx.toastMessages('error', '', errorMessages)
            return false
        }
        
        return true
    }

    const handleSave = () => {
        // Executar validações
        if (!validateBeforeSave()) {
            return
        }

        ctx.setIsLoad(true)
        setHasUnsavedChanges(false)
        
        const saveData = { ...data }
        delete saveData.Loading_LocationCode
        delete saveData.Discharge_LocationCode
        delete saveData.Receipt_LocationCode
        delete saveData.Delivery_LocationCode
        delete saveData.EquipmentClass_Code
        delete saveData.EquipmentType_Code
        if (!saveData.ClauseDocument) saveData.ClauseDocument = null

        if (isCreateMode || operation === 'Copy') {
            delete saveData.Id
            bufferService.post(saveData).then(response => {
                if (response.name === 'AxiosError') {
                    ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
                    ctx.setIsLoad(false)
                } else {
                    ctx.toastMessages('success', '', t('ClauseCreatedSuccess', { code: data.ClauseCode }))
                    ctx.setIsLoad(false)
                    handleTabClose(JSON.parse(id))
                }
            })
        } else if (isEditMode) {
            bufferService.update(data.Id, saveData).then(response => {
                if (response.name === 'AxiosError') {
                    ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
                    ctx.setIsLoad(false)
                } else {
                    ctx.toastMessages('success', '', t('ClauseUpdatedSuccess', { code: data.ClauseCode }))
                    ctx.setIsLoad(false)
                    handleTabClose(JSON.parse(id))
                }
            })
        }
    }

    const handleRefresh = () => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm(t('UnsavedChangesWarning'))
            if (!confirmed) return
        }

        setHasUnsavedChanges(false)
        setErrors({})

        if (isCreateMode) {
            handleOpenCreate()
        } else if (rowId) {
            handleOpenEdit(rowId, operation)
        }
    }

    const handleBack = () => {
        if (hasUnsavedChanges && !isViewMode) {
            const confirmed = window.confirm(t('UnsavedChangesExit'))
            if (!confirmed) return
        }
        handleTabClose(JSON.parse(id))
    }

    const handleAddDetail = () => {
        /*
        if (!newDetail.DetailDescription?.trim()) {
            ctx.toastMessages('error', '', t('DetailDescriptionRequired'))
            return
        }
        */
       
        ctx.setIsLoad(true)
        const payload = {
            ...newDetail,
            IdTransportClause: data.Id,
            ClauseStartDate: data.StartDate || new Date().toISOString().split('T')[0]
        }
        detailService.post(payload).then(response => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
            } else {
                ctx.toastMessages('success', '', t('DetailAddedSuccess'))
                setNewDetail({})
                loadDetails(data.Id)
            }
            ctx.setIsLoad(false)
        }).catch(err => {
            ctx.toastMessages('error', '', err.message)
            ctx.setIsLoad(false)
        })
    }

    const handleDeleteDetail = (detailId) => {
        if (!window.confirm(t('ConfirmDeleteDetail'))) return
        ctx.setIsLoad(true)
        detailService.delete(detailId).then(response => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', response?.response?.data?.Errors || response.message)
            } else {
                ctx.toastMessages('success', '', t('DetailDeletedSuccess'))
                loadDetails(data.Id)
            }
            ctx.setIsLoad(false)
        }).catch(err => {
            ctx.toastMessages('error', '', err.message)
            ctx.setIsLoad(false)
        })
    }

    return (
        <div className='transportClauseCrudContainer'>
            <M_Header
                element={
                    <>
                        <M_Button
                            tooltip={t("Back")}
                            label={t("Back")}
                            icon={<i className="pi pi-arrow-left iconTable"></i>}
                            onClick={() => handleBack()}
                        />
                        <M_Button
                            tooltip={t("Refresh")}
                            label={t("Refresh")}
                            icon={<i className="pi pi-refresh iconTable"></i>}
                            onClick={() => handleRefresh()}
                        />
                        {!isViewMode &&
                            <M_Button
                                tooltip={t("Save")}
                                label={t("Save")}
                                icon={<i className="pi pi-save iconTable"></i>}
                                onClick={() => handleSave()}
                            />
                        }
                    </>
                }
            />

            <div className='transportClauseFormContainer'>
                {/* Row 1: TransportClause (2/3) + ServiceRestrictions (1/3) */}
                <div className='twoThirdsOneThirdSection'>
                    <M_Fieldset label={t('TransportClause')} elements={
                        <div className='transportClauseDetailsGrid'>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('ClauseCode')}
                                    value={data.ClauseCode || ''}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        if (value.length <= 8) {
                                            handleChange('ClauseCode', value)
                                        }
                                    }}
                                    disabled={isViewMode}
                                    required
                                    error={!!errors.ClauseCode}
                                    helperText={errors.ClauseCode || t('CharactersCount', { count: (data.ClauseCode || '').length, max: 8 })}
                                    placeholder={t('ClauseCodePlaceholder')}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('ClauseName')}
                                    value={data.ClauseName || ''}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        if (value.length <= 64) {
                                            handleChange('ClauseName', value)
                                        }
                                    }}
                                    disabled={isViewMode}
                                    error={!!errors.ClauseName}
                                    helperText={errors.ClauseName || t('CharactersCount', { count: (data.ClauseName || '').length, max: 64 })}
                                    placeholder={t('ClauseNamePlaceholder')}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('ClauseOrder')}
                                    type="number"
                                    value={data.ClauseOrder || ''}
                                    onChange={(e) => handleChange('ClauseOrder', e.target.value)}
                                    disabled={isViewMode}
                                    error={!!errors.ClauseOrder}
                                    helperText={errors.ClauseOrder || t('ClauseOrderHelperText')}
                                    placeholder={t('OrderPlaceholder')}
                                />
                            </div>
                        </div>
                    } />

                    <M_Fieldset label={t('ServiceRestrictions')} elements={
                        <div className='serviceRestrictionsGrid'>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('TradeLineCode')}
                                    value={data.TradeLineCode || ''}
                                    onChange={(e) => handleChange('TradeLineCode', e.target.value)}
                                    disabled={isViewMode}
                                    placeholder={t('TradeLineCodePlaceholder')}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('TradeCode')}
                                    value={data.TradeCode || ''}
                                    onChange={(e) => handleChange('TradeCode', e.target.value)}
                                    disabled={isViewMode}
                                    placeholder={t('TradeCodePlaceholder')}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('TradeBound')}
                                    value={data.TradeBound || ''}
                                    onChange={(e) => handleChange('TradeBound', e.target.value)}
                                    disabled={isViewMode}
                                    placeholder={t('TradeBoundPlaceholder')}
                                />
                            </div>
                        </div>
                    } />
                </div>

                {/* Row 2: LineRestrictions (1/3) + PortRestrictions (2/3) */}
                <div className='oneThirdTwoThirdsSection'>
                    <M_Fieldset label={t('LineRestrictions')} elements={
                        <div className='lineRestrictionsGrid'>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('LineCode')}
                                    value={data.LineCode || ''}
                                    onChange={(e) => handleChange('LineCode', e.target.value)}
                                    disabled={isViewMode}
                                    placeholder={t('LineCodePlaceholder')}
                                />
                            </div>
                        </div>
                    } />

                    <M_Fieldset label={t('PortRestrictions')} elements={
                        <div className='portRestrictionsGrid'>
                            <div className='fieldItem'>
                                <M_DrawerTableTextField
                                    tableLabel="Location"
                                    label={t('LoadingPort')}
                                    value={data.LoadingId ? { Id: data.LoadingId, LocationCode: data.Loading_LocationCode } : null}
                                    entity={'location'}
                                    filterField="LocationCode"
                                    labelFields={['LocationCode']}
                                    onSubmitValue={(obj) => {
                                        if (obj) {
                                            handleChange('LoadingId', obj.Id)
                                            handleChange('Loading_LocationCode', obj.LocationCode)
                                        } else {
                                            handleChange('LoadingId', '')
                                            handleChange('Loading_LocationCode', '')
                                        }
                                    }}
                                    disabled={isViewMode}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_DrawerTableTextField
                                    tableLabel="Location"
                                    label={t('DischargePort')}
                                    value={data.DischargeId ? { Id: data.DischargeId, LocationCode: data.Discharge_LocationCode } : null}
                                    entity={'location'}
                                    filterField="LocationCode"
                                    labelFields={['LocationCode']}
                                    onSubmitValue={(obj) => {
                                        if (obj) {
                                            handleChange('DischargeId', obj.Id)
                                            handleChange('Discharge_LocationCode', obj.LocationCode)
                                        } else {
                                            handleChange('DischargeId', '')
                                            handleChange('Discharge_LocationCode', '')
                                        }
                                    }}
                                    disabled={isViewMode}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_DrawerTableTextField
                                    tableLabel="Location"
                                    label={t('ReceiptPlace')}
                                    value={data.ReceiptId ? { Id: data.ReceiptId, LocationCode: data.Receipt_LocationCode } : null}
                                    entity={'location'}
                                    filterField="LocationCode"
                                    labelFields={['LocationCode']}
                                    onSubmitValue={(obj) => {
                                        if (obj) {
                                            handleChange('ReceiptId', obj.Id)
                                            handleChange('Receipt_LocationCode', obj.LocationCode)
                                        } else {
                                            handleChange('ReceiptId', '')
                                            handleChange('Receipt_LocationCode', '')
                                        }
                                    }}
                                    disabled={isViewMode}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_DrawerTableTextField
                                    tableLabel="Location"
                                    label={t('DeliveryPlace')}
                                    value={data.DeliveryId ? { Id: data.DeliveryId, LocationCode: data.Delivery_LocationCode } : null}
                                    entity="location"
                                    filterField="LocationCode"
                                    labelFields={['LocationCode']}
                                    onSubmitValue={(obj) => {
                                        if (obj) {
                                            handleChange('DeliveryId', obj.Id)
                                            handleChange('Delivery_LocationCode', obj.LocationCode)
                                        } else {
                                            handleChange('DeliveryId', '')
                                            handleChange('Delivery_LocationCode', '')
                                        }
                                    }}
                                    disabled={isViewMode}
                                />
                            </div>
                        </div>
                    } />
                </div>

                {/* Row 3: DateRestrictions (1/3) + EquipmentRestrictions (1/3) + Document (1/3) */}
                <div className='threeEqualSection'>
                    <M_Fieldset label={t('DateRestrictions')} elements={
                        <div className='dateRestrictionsGrid'>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('StartDate')}
                                    type="date"
                                    value={data.StartDate || ''}
                                    onChange={(e) => handleChange('StartDate', e.target.value)}
                                    disabled={isViewMode}
                                    required
                                    error={!!errors.StartDate}
                                    helperText={errors.StartDate || t('StartDateHelperText')}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_InputTextField
                                    label={t('EndDate')}
                                    type="date"
                                    value={data.EndDate || ''}
                                    onChange={(e) => handleChange('EndDate', e.target.value)}
                                    disabled={isViewMode}
                                    error={!!errors.EndDate}
                                    helperText={errors.EndDate || t('EndDateHelperText')}
                                />
                            </div>
                        </div>
                    } />

                    <M_Fieldset label={t('EquipmentRestrictions')} elements={
                        <div className='equipmentRestrictionsGrid'>
                            <div className='fieldItem'>
                                <M_DrawerTableTextField
                                    tableLabel="EquipmentClass"
                                    label={t('EquipmentClass')}
                                    value={data.EquipmentClassId ? { Id: data.EquipmentClassId, EquipmentCode: data.EquipmentClass_Code } : null}
                                    entity="equipmentClass"
                                    filterField="EquipmentCode"
                                    labelFields={['EquipmentCode']}
                                    onSubmitValue={(obj) => {
                                        if (obj) {
                                            handleChange('EquipmentClassId', obj.Id)
                                            handleChange('EquipmentClass_Code', obj.EquipmentCode)
                                        } else {
                                            handleChange('EquipmentClassId', '')
                                            handleChange('EquipmentClass_Code', '')
                                        }
                                    }}
                                    disabled={isViewMode}
                                />
                            </div>
                            <div className='fieldItem'>
                                <M_DrawerTableTextField
                                    tableLabel="EquipmentType"
                                    label={t('EquipmentType')}
                                    value={data.EquipmentTypeId ? { 
                                        Id: data.EquipmentTypeId,
                                        EquipmentTypeCode: data.EquipmentType_Code || '',
                                        EquipmentTypeLenght: data.EquipmentType_Lenght || '',
                                    } : null}
                                    entity={'equipmentType'}
                                    filterField={'EquipmentTypeCodePlusLenght'}
                                    labelFields={['EquipmentTypeLenght', 'EquipmentTypeCode']}
                                    onSubmitValue={(obj) => {
                                        if (obj) {
                                            handleChange('EquipmentTypeId', obj.Id)
                                            handleChange('EquipmentType_Code', obj.EquipmentTypeCodePlusLenght)
                                        } else {
                                            handleChange('EquipmentTypeId', '')
                                            handleChange('EquipmentType_Code', '')
                                        }
                                    }}
                                    disabled={isViewMode}
                                />
                            </div>
                        </div>
                    } />

                    <M_Fieldset label={t('DocumentSection')} elements={
                        <div className='documentSection'>
                            <M_Select
                                label={t('DocumentReference')}
                                value={data.ClauseDocument || ''}
                                onChange={(e) => handleChange('ClauseDocument', e.target.value || null)}
                                disabled={isViewMode}
                                error={!!errors.ClauseDocument}
                                helperText={errors.ClauseDocument}
                                options={clauseDocumentOptions}
                                hasNone={true}
                            />
                        </div>
                    } />
                </div>

                {/* Transport Clause Details */}
                {(isEditMode || isViewMode) && data.Id && (
                    <M_Fieldset label={t('TransportClauseDetailsDescription')} elements={
                        <div className='detailsTableContainer'>
                            {/* Add Detail Form */}
                            {!isViewMode && (
                                <div className='detailAddRow33'>
                                    <M_InputTextField
                                        label={t('DetailDescription')}
                                        value={newDetail.DetailDescription}
                                        onChange={(e) => setNewDetail(prev => ({ ...prev, DetailDescription: e.target.value }))}
                                        placeholder={t('DetailDescription')}
                                        size="100%"
                                        submit={handleAddDetail}
                                    />
                                    <M_Button
                                        hoverEffect
                                        normal
                                        label={t('AddDescription')}
                                        icon={<img src='/icons/svgsDaNet/plus-square.svg' alt={t('AddDescription')} style={{ filter: 'invert(42%) sepia(44%) saturate(4434%) hue-rotate(74deg) brightness(97%) contrast(101%)' }} className='iconCreateBooking' />}
                                        tooltip={t('AddDescription')}
                                        onClick={handleAddDetail}
                                        borderColor={'var(--color-grey)'}
                                        labelColor={'black'}
                                        bold
                                    />
                                </div>
                            )}
                            <DataTable
                                value={details}
                                emptyMessage={t('NoDetailsFound')}
                                size="small"
                                className='BLTable'
                                breakpoint='0px'
                                paginator
                                rows={5}
                                dataKey="Id"
                            >
                                <Column field="DetailDescription" header={t('DetailDescription')} />
                                {!isViewMode && (
                                    <Column
                                        style={{ width: '60px' }}
                                        body={(row) => (
                                            <M_Button
                                                icon={<i className="pi pi-trash"></i>}
                                                onClick={() => handleDeleteDetail(row.Id)}
                                                tooltip={t('Delete')}
                                            />
                                        )}
                                    />
                                )}
                            </DataTable>
                        </div>
                    } />
                )}
            </div>
        </div>
    )
}
